<template>
  <pf-form-group
    ref="el"
    data-ouia-component-type="FormGroup"
    :data-ouia-component-id="ouiaId || name"
    :class="[presentedState ? `has-${presentedState}` : '']"
    :helper-text="help"
  >
    <label v-if="noLabel" class="pf-v5-u-hidden pf-v6-u-hidden">{{ name }}</label>

    <template v-if="!noLabel" #label>
      <slot name="label">{{ name }}</slot>
    </template>

    <template v-if="$slots['label-info']" #label-info>
      <slot name="label-info" />
    </template>

    <template v-if="$slots['helper-text-invalid-icon']" #helper-text-invalid-icon>
      <slot name="helper-text-invalid-icon" />
    </template>

    <template v-if="$slots['helper-text-invalid']" #helper-text-invalid>
      <slot name="helper-text-invalid" />
    </template>

    <template v-if="$slots['helper-text-icon']" #helper-text-icon>
      <slot name="helper-text-icon" />
    </template>

    <template v-if="$slots['helper-text']" #helper-text>
      <slot name="helper-text" />
    </template>

    <template v-if="fieldHelp || $slots['field-help']" #label-icon>
      <pf-tooltip v-if="fieldHelp || $slots['field-help']">
        <template #content>
          <slot name="field-help">{{ fieldHelp }}</slot>
        </template>
        <help-icon />
      </pf-tooltip>
      <slot name="label-icon" v-else />
    </template>

    <slot />
  </pf-form-group>
</template>

<script lang="ts">
export type FormState = 'success' | 'warning' | 'error' | '';

export interface Props extends /* @vue-ignore */ Omit<PfFormGroupProps, 'label' | 'helper-text'> {
  name: string;
  noLabel?: boolean;
  help?: string;
  fieldHelp?: string;
  disabled?: boolean;
  state?: FormState;
  ouiaId?: string;
}
</script>

<script lang="ts" setup>
import { FormGroupDisabledInjectionKey } from '@common/mixins/XFormElement';
import { computed, reactive, type Ref, ref, provide, onMounted, onBeforeUnmount } from 'vue';
import HelpIcon from '@vue-patternfly/icons/help-icon';
import type { default as PfFormGroup, Props as PfFormGroupProps } from '@vue-patternfly/core/components/Form/FormGroup.vue';

defineOptions({
  name: 'FormGroup',
});

const props = defineProps<Props>();

defineSlots<{
  default: (props?: Record<never, never>) => any;
  'field-help'?: (props: Record<string, never>) => any;
  label?: (props?: Record<never, never>) => any;
  'label-icon'?: (props?: Record<never, never>) => any;
  'label-info'?: (props?: Record<never, never>) => any;
  'helper-text-invalid-icon'?: (props?: Record<never, never>) => any;
  'helper-text-invalid'?: (props?: Record<never, never>) => any;
  'helper-text-icon'?: (props?: Record<never, never>) => any;
  'helper-text'?: (props?: Record<never, never>) => any;
}>();

provide(FormGroupDisabledInjectionKey, computed(() => props.disabled));

const el: Ref<InstanceType<typeof PfFormGroup> | undefined> = ref();
const inputListeners: HTMLSelectElement[] = reactive([]);
const autoState: Ref<FormState> = ref('');

const presentedState = computed(() => props.state ?? autoState.value);

onMounted(() => {
  inputListeners.splice(0, inputListeners.length);

  if (!el.value) {
    return;
  }

  for (const select of (el.value.$el as HTMLElement).querySelectorAll('select')) {
    select.addEventListener('invalid', onInvalid);
    select.addEventListener('change', resetValidity);
    inputListeners.push(select);
  }
});

onBeforeUnmount(() => {
  for (const el of inputListeners) {
    el.removeEventListener('invalid', onInvalid);
    el.removeEventListener('change', resetValidity);
  }
});

function onInvalid() {
  autoState.value = 'error';
}

function resetValidity() {
  autoState.value = '';
}
</script>
